import { DvGridComponent, DvToolbarAction } from '@devinco/ui/grid';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, Validators } from '@angular/forms';
import { RestApiService } from '@core/services/rest-api.service';
import { UserRolesAndPermissionsService } from '@core/services/user-roles-and-permissions.service';
import {
  DvDynamicFormElement,
  DvDynamicFormModule,
  DvDynamicFormUtils,
  DvFormUtils,
  DvTimePickerElementParams,
  FormOf,
  DvElementType,
} from '@devinco/ui/legacy';
import { GridOptionsService } from '@features/vt-grid/grid-options/grid-options.service';
import { GridOptions } from '@features/vt-grid/interfaces/grid-options';
import { Entity } from '@shared/constants/entity';
import { RolesAndPermissions } from '@shared/constants/roles-and-permissions';
import { TranslateModule } from '@ngx-translate/core';
import { WeekdaysForm } from '@pages/reports-page/components/report-tabs/report-information-tab/interfaces';
import { TripFilterDto } from '@pages/reports-page/interfaces';
import { TripsGridService } from '@pages/trips-page/services/trips-grid.service';
import { DirectivesModule } from '@shared/directives/directives.module';
import { AgGridModule } from 'ag-grid-angular';
import {
  ColDef,
  FilterChangedEvent,
  GridApi,
  GridReadyEvent,
} from 'ag-grid-community';
import { take } from 'rxjs';
import {
  excludedRequestValues,
  FilterInterval,
  PeriodInterval,
} from './constants';
import {
  DataFilterCreateEditForm,
  EditFilterDialogData,
  EditFilterDialogResult,
} from './interfaces';
import { DataFilterElementsService } from './services/data-filter-elements.service';
import { ExtraFilterDataSaveService } from './services/filter-data-save.service';
import TripDataFilterFormUtils from './utils/trip-data-filter-form.utils';
import { PipesModule } from '@shared/pipes/pipes.module';
import {
  DvButtonComponent,
  DvDialogContent,
  DvDialogContentComponent,
} from '@devinco/ui/dds';
import { vtPresetActions } from '@features/vt-grid/presets/preset-actions';
import { TripDto } from '@pages/trips-page/components/trips-grid/interfaces';

@Component({
  selector: 'app-edit-trip-filter-dialog',
  templateUrl: './edit-trip-filter-dialog.component.html',
  styleUrls: ['./edit-trip-filter-dialog.component.scss'],
  imports: [
    CommonModule,
    DvDynamicFormModule,
    PipesModule,
    TranslateModule,
    AgGridModule,
    DvGridComponent,
    DirectivesModule,
    DvDialogContentComponent,
    DvButtonComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [GridOptionsService],
})
export class EditTripFilterDialogComponent
  extends DvDialogContent<EditFilterDialogData, EditTripFilterDialogComponent>
  implements OnInit
{
  private dataFilterElementsService = inject(DataFilterElementsService);
  private api = inject(RestApiService);
  private gridOptionsService = inject(GridOptionsService);
  private dataSaveService = inject(ExtraFilterDataSaveService);
  private tripsGridService = inject(TripsGridService);
  private permissionsService = inject(UserRolesAndPermissionsService);
  private cdRef = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  formGroup: FormGroup<FormOf<DataFilterCreateEditForm>>;
  filterNameElements: DvDynamicFormElement[];
  driverAndDepartmentElements: DvDynamicFormElement[];
  periodElements: DvDynamicFormElement[];
  intervalElements: DvDynamicFormElement[];
  distanceElements: DvDynamicFormElement[];
  permissionElements: DvDynamicFormElement[];
  allElements: DvDynamicFormElement[];

  isLoading = signal<boolean>(false);

  gridOptions: GridOptions;
  actions: DvToolbarAction[];
  popupParent: HTMLElement | null = document.querySelector(
    'mat-dialog-container'
  );
  gridApi: GridApi;

  /** user should not be able to filter on driver and user column values
   * since those values come from form dropdowns (ensures single source of truth)
   */
  private readonly hiddenGridColumns: (keyof TripDto)[] = [
    'DepartmentID',
    'DepartmentDescription',
    'UserID',
    'Username',
  ];

  ngOnInit(): void {
    this.actions = [vtPresetActions.reset, vtPresetActions.fit];
    this.gridOptions = {
      ...this.gridOptionsService.vtCore(this.getColumns()),
    };
    this.setElements();
    this.fetchData();
  }

  onGridFilterChanged(event: FilterChangedEvent): void {
    if (event.source === 'columnFilter') {
      this.formGroup.markAsDirty();
    }
  }

  onGridReady(event: GridReadyEvent): void {
    this.gridApi = event.api;
    if (this.gridOptions.context.extraFilter) {
      event.api.setFilterModel(this.gridOptions.context.extraFilter);
    }
  }

  performSave(): void {
    const values = DvFormUtils.getDirtyValues(
      this.formGroup
    ) as DataFilterCreateEditForm;
    this.api
      .upsert(
        Entity.DataFilter,
        this.data.entityData?.DataFilterID || '0',
        this.dataSaveService.prepareDataForSave(
          values,
          this.driverAndDepartmentElements,
          this.gridApi.getFilterModel(),
          TripDataFilterFormUtils.constructWeekDayArray(
            this.formGroup.value as DataFilterCreateEditForm
          )
        )
      )
      .subscribe(_ => {
        this.dialogRef.onConfirm({
          hasFilterNameChanged: !!('Name' in values),
          name: values.Name,
        } as EditFilterDialogResult);
      });
  }

  cancel(): void {
    this.dialogRef.onCancel();
  }

  private getColumns(): ColDef[] {
    return this.tripsGridService
      .getColumnDefs()
      .filter(
        colDef =>
          !this.hiddenGridColumns.includes(colDef.field! as keyof TripDto)
      )
      .map(colDef => ({
        ...colDef,
        hide: false,
        pinned: false,
        sort: undefined,
      }));
  }

  private setElements(): void {
    this.filterNameElements =
      this.dataFilterElementsService.getFilterNameElements();
    this.driverAndDepartmentElements =
      this.dataFilterElementsService.departmentAndDriverElements();
    this.periodElements = this.dataFilterElementsService.getPeriodElements();
    this.intervalElements = this.dataFilterElementsService
      .getIntervalElements(4)
      .map((el, indx) => {
        if (el.options.elementType === DvElementType.TimePicker) {
          return {
            ...el,
            options: {
              ...el.options,
              // making sure that time picker box would have unique element id
              // since there's one on trips page already before this modal is opened
              pickerElementId: indx + el.options.key!,
            } as DvTimePickerElementParams,
          };
        } else {
          return el;
        }
      });
    this.distanceElements =
      this.dataFilterElementsService.getDistanceElements();
    this.permissionElements =
      this.dataFilterElementsService.getPermissionElements();

    this.allElements = [
      ...this.filterNameElements,
      ...this.driverAndDepartmentElements,
      ...this.periodElements,
      ...this.intervalElements,
      ...this.distanceElements,
      ...this.permissionElements,
    ];
  }

  private fetchData(): void {
    const entityId = this.data.entityData?.DataFilterID;
    if (entityId) {
      this.isLoading.set(true);
      this.api
        .getInfo<TripFilterDto>(Entity.DataFilter, String(entityId), [
          ...DvDynamicFormUtils.getKeysForRequest(this.allElements).filter(
            field => !excludedRequestValues.includes(field)
          ),
          'FilterModel',
        ])
        .pipe(take(1))
        .subscribe(resp => {
          this.setupForm(resp);
          this.setupEvents();
          this.isLoading.set(false);
        });
    } else {
      this.setupForm();
      this.setupEvents();
      this.setupStartEndDayFields(undefined);
    }
  }

  private setupForm(data?: TripFilterDto): void {
    this.formGroup = DvFormUtils.createFormGroup(this.allElements, true);

    if (data) {
      DvDynamicFormUtils.setFirstData(this.allElements, this.formGroup, data);
      data.WeekDay?.forEach(day => {
        this.formGroup.controls[String(day) as keyof WeekdaysForm].setValue(
          true,
          { emitEvent: false }
        );
      });

      if (!data.Interval) {
        this.formGroup.controls.Interval?.setValue(FilterInterval.None, {
          emitEvent: false,
        });
      }
      this.setupStartEndDayFields(data.Period!);
      this.intervalElements = [
        ...this.dataFilterElementsService.toggleIntervalFieldsVisibility(
          this.intervalElements,
          this.formGroup
        ),
      ];

      TripDataFilterFormUtils.setMultiselectDropdownValues(
        data.Driver || [],
        data.Department || [],
        this.formGroup,
        this.driverAndDepartmentElements
      );
      this.gridOptions.context.extraFilter = data.AgGridFilter;
    }
    this.handleAccessRestrictions();
    this.cdRef.detectChanges();
  }

  private setupEvents(): void {
    this.listenToIntervalValueChanges();
    this.showHidePeriodStartEndDays();
  }

  private handleAccessRestrictions(): void {
    this.permissionsService
      .hasPermissions([RolesAndPermissions.FilterEditExtended])
      .pipe(take(1))
      .subscribe(hasEditExtended => {
        if (!hasEditExtended) {
          this.formGroup.controls.VisibleToAllUsers?.disable();
        }
      });
  }

  private showHidePeriodStartEndDays(): void {
    this.formGroup.controls.Period?.valueChanges.subscribe(value => {
      const obj = value as unknown as PeriodInterval;
      this.setupStartEndDayFields(obj);
    });
  }

  private setupStartEndDayFields(
    periodValue: PeriodInterval | undefined
  ): void {
    const startDay = this.allElements.filter(
      q => q.options.key === 'StartDay'
    )[0];
    const endDay = this.allElements.filter(q => q.options.key === 'EndDay')[0];
    const isCustomSelected = periodValue === PeriodInterval.Custom;
    startDay.options.hidden = !isCustomSelected;
    endDay.options.hidden = !isCustomSelected;
    if (isCustomSelected) {
      this.formGroup.controls.StartDay?.addValidators([
        Validators.required,
        Validators.min(1),
        Validators.max(31),
      ]);
      this.formGroup.controls.StartDay?.updateValueAndValidity();
      this.formGroup.controls.EndDay?.addValidators([
        Validators.required,
        Validators.min(1),
        Validators.max(31),
      ]);
      this.formGroup.controls.EndDay?.updateValueAndValidity();
    } else {
      this.formGroup.controls.EndDay?.clearValidators();
      this.formGroup.controls.EndDay?.updateValueAndValidity();
      this.formGroup.controls.StartDay?.clearValidators();
      this.formGroup.controls.StartDay?.updateValueAndValidity();
    }
  }

  private listenToIntervalValueChanges(): void {
    this.formGroup.controls.Interval?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(_ => {
        this.intervalElements = [
          ...this.dataFilterElementsService.toggleIntervalFieldsVisibility(
            this.intervalElements,
            this.formGroup
          ),
        ];
      });
  }
}
